import { useState, useEffect, useCallback } from 'react';
import apiServerClient from '@/lib/apiServerClient.js';

// Global state for request deduplication
let fetchPromise = null;
let globalCurrencyData = null;
const CACHE_KEY = 'conversionDataCache';
const CACHE_TTL = 60000; // 60 seconds

export function useConverterCache() {
  const [currencyData, setCurrencyData] = useState(() => {
    if (globalCurrencyData) return globalCurrencyData;
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        globalCurrencyData = parsed.rates;
        return parsed.rates;
      }
    } catch (e) {
      console.error('Failed to parse cache', e);
    }
    return null;
  });
  
  const [isCurrencyLoading, setIsCurrencyLoading] = useState(false);
  const [error, setError] = useState(null);

  const updateCurrencyData = useCallback(async () => {
    if (fetchPromise) {
      setIsCurrencyLoading(true);
      try {
        const data = await fetchPromise;
        setCurrencyData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsCurrencyLoading(false);
      }
      return fetchPromise;
    }

    setIsCurrencyLoading(true);
    setError(null);

    fetchPromise = apiServerClient.fetch('/exchange-rates')
      .then(async (res) => {
        if (!res.ok) throw new Error('Falha ao obter taxas de câmbio');
        const data = await res.json();
        
        const cacheData = {
          rates: data.rates,
          timestamp: Date.now()
        };
        
        localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
        globalCurrencyData = data.rates;
        setCurrencyData(data.rates);
        return data.rates;
      })
      .catch(err => {
        console.error('Error fetching rates:', err);
        setError('Erro ao carregar taxas de câmbio. Tente novamente mais tarde.');
        throw err;
      })
      .finally(() => {
        setIsCurrencyLoading(false);
        fetchPromise = null;
      });

    return fetchPromise;
  }, []);

  const initializeCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      const now = Date.now();
      let needsUpdate = true;
      
      if (cached) {
        const parsed = JSON.parse(cached);
        if (now - parsed.timestamp < CACHE_TTL) {
          needsUpdate = false;
          globalCurrencyData = parsed.rates;
          setCurrencyData(parsed.rates);
        }
      }
      
      if (needsUpdate) {
        updateCurrencyData();
      }
    } catch (e) {
      updateCurrencyData();
    }

    // Set up the 60-second refresh interval
    const intervalId = setInterval(() => {
      updateCurrencyData();
    }, CACHE_TTL);

    return () => clearInterval(intervalId);
  }, [updateCurrencyData]);

  const getCachedData = useCallback((category) => {
    if (category === 'currency') return currencyData;
    // Other categories use static data from conversionData.js, so they are instantly available
    return null;
  }, [currencyData]);

  return {
    conversionData: currencyData, // Alias for backward compatibility/general use
    currencyData,
    isCurrencyLoading,
    error,
    initializeCache,
    updateCurrencyData,
    getCachedData
  };
}

export function useConverter(selectedConverter) {
  const [inputValue, setInputValue] = useState('');
  const [outputValue, setOutputValue] = useState('');
  const [fromUnit, setFromUnit] = useState('');
  const [toUnit, setToUnit] = useState('');
  
  const { currencyData: exchangeRates, isCurrencyLoading, error: ratesError } = useConverterCache();

  // Reset units when converter changes
  useEffect(() => {
    if (selectedConverter && selectedConverter.units && selectedConverter.units.length > 0) {
      setFromUnit(selectedConverter.units[0].id);
      setToUnit(selectedConverter.units.length > 1 ? selectedConverter.units[1].id : selectedConverter.units[0].id);
      setInputValue('');
      setOutputValue('');
    }
  }, [selectedConverter]);

  // Handle conversion logic
  useEffect(() => {
    if (!inputValue || inputValue === '') {
      setOutputValue('');
      return;
    }

    if (!selectedConverter || !fromUnit || !toUnit) return;

    let result = 0;

    // Conversores com fórmula customizada (camiseta, calça, tênis, etc.)
    if (typeof selectedConverter.customConvert === 'function') {
      result = selectedConverter.customConvert(inputValue, fromUnit, toUnit);
      setOutputValue(result === undefined || result === null ? '' : String(result));
      return;
    }

    const numValue = parseFloat(inputValue);
    if (isNaN(numValue)) {
      setOutputValue('');
      return;
    }

    if (selectedConverter.isTemperature) {
      result = convertTemperature(numValue, fromUnit, toUnit);
    } else if (selectedConverter.isCurrency) {
      if (!exchangeRates) return; // Wait for rates to load from cache/API
      
      const sourceRate = exchangeRates[fromUnit];
      const targetRate = exchangeRates[toUnit];
      
      if (sourceRate && targetRate) {
        result = (numValue * targetRate) / sourceRate;
      }
    } else {
      const fromU = selectedConverter.units.find(u => u.id === fromUnit);
      const toU = selectedConverter.units.find(u => u.id === toUnit);
      
      if (fromU && toU) {
        result = (numValue * fromU.factor) / toU.factor;
      }
    }

    if (Math.abs(result) < 0.000001 && result !== 0) {
      setOutputValue(result.toExponential(6));
    } else if (Math.abs(result) > 1000000) {
      setOutputValue(result.toExponential(6));
    } else {
      // Limit to max 8 decimals but remove trailing zeros for cleanliness
      setOutputValue(parseFloat(result.toFixed(8)).toString());
    }
  }, [inputValue, fromUnit, toUnit, selectedConverter, exchangeRates]);

  const convertTemperature = (value, from, to) => {
    let celsius;

    if (from === 'c') {
      celsius = value;
    } else if (from === 'f') {
      celsius = (value - 32) * 5 / 9;
    } else if (from === 'k') {
      celsius = value - 273.15;
    }

    if (to === 'c') {
      return celsius;
    } else if (to === 'f') {
      return celsius * 9 / 5 + 32;
    } else if (to === 'k') {
      return celsius + 273.15;
    }

    return value;
  };

  const swapUnits = () => {
    setFromUnit(toUnit);
    setToUnit(fromUnit);
    setInputValue(outputValue);
  };

  const clearInput = () => {
    setInputValue('');
    setOutputValue('');
  };

  return {
    inputValue,
    setInputValue,
    outputValue,
    fromUnit,
    setFromUnit,
    toUnit,
    setToUnit,
    swapUnits,
    clearInput,
    isLoadingRates: selectedConverter?.isCurrency ? isCurrencyLoading : false,
    ratesError: selectedConverter?.isCurrency ? ratesError : null
  };
}